import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OwnerContext } from "../../context/OwnerContext";
import OwnerLayout from "./OwnerLayout";
import useOwnerDashboard from "./useOwnerDashboard";
import Widget from "../commons/widgets/Widget";
import WidgetGrid from "../commons/widgets/WidgetGrid";
import { FaDog, FaCalendarAlt, FaDumbbell, FaClipboardList, FaFileAlt } from 'react-icons/fa';
import { FiMail, FiCheck, FiX } from 'react-icons/fi';
import axios from '../../axiosConfig';
import '../../styles/OwnerDashboard.css';

const OwnerDashboard = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation('dashboard');
    const { owner, loading: ownerLoading, error: ownerError, refreshOwnerData } = useContext(OwnerContext);
    const { dashboardData, loading: dashboardLoading, error: dashboardError, refresh } = useOwnerDashboard();
    const [pendingInvitations, setPendingInvitations] = useState([]);
    const [invitationLoading, setInvitationLoading] = useState(false);

    const fetchInvitations = useCallback(async () => {
        try {
            const response = await axios.get('/api/v1/owner/invitations/pending');
            setPendingInvitations(response.data || []);
        } catch {
            // silently fail
        }
    }, []);

    useEffect(() => {
        fetchInvitations();
    }, [fetchInvitations]);

    const handleAcceptInvitation = async (invitationId) => {
        try {
            setInvitationLoading(true);
            await axios.post(`/api/v1/owner/invitations/${invitationId}/accept`);
            setPendingInvitations(prev => prev.filter(inv => inv.id !== invitationId));
            refreshOwnerData();
            refresh();
        } catch {
            // error handled
        } finally {
            setInvitationLoading(false);
        }
    };

    const handleRejectInvitation = async (invitationId) => {
        try {
            setInvitationLoading(true);
            await axios.post(`/api/v1/owner/invitations/${invitationId}/reject`);
            setPendingInvitations(prev => prev.filter(inv => inv.id !== invitationId));
        } catch {
            // error handled
        } finally {
            setInvitationLoading(false);
        }
    };

    if (ownerLoading || dashboardLoading) {
        return (
            <OwnerLayout>
                <div className="owner-dashboard-container">
                    <div className="owner-dashboard-loading">{t('owner.loading')}</div>
                </div>
            </OwnerLayout>
        );
    }

    if (ownerError) {
        return (
            <OwnerLayout>
                <div className="owner-dashboard-container">
                    <div className="owner-dashboard-error">{ownerError}</div>
                </div>
            </OwnerLayout>
        );
    }

    const welcomeName = `${owner?.firstName} ${owner?.lastName}`;
    const dogs = dashboardData?.dogs || [];

    const dateLocale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString(dateLocale, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
        });
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleTimeString(dateLocale, {
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getProgressVariant = (percentage) => {
        if (percentage >= 75) return 'success';
        if (percentage >= 50) return 'primary';
        if (percentage >= 25) return 'warning';
        return 'danger';
    };

    return (
        <OwnerLayout>
            <div className="owner-dashboard-container">
                <div className="owner-dashboard-header">
                    <h1>{t('owner.welcome', { name: welcomeName })}</h1>
                    <p className="owner-dashboard-subtitle">{t('owner.subtitle')}</p>
                </div>

                {pendingInvitations.length > 0 && (
                    <div className="owner-dashboard-invitations">
                        {pendingInvitations.map((invitation) => (
                            <div key={invitation.id} className="owner-dashboard-invitation-card">
                                <div className="owner-dashboard-invitation-icon">
                                    <FiMail size={24} />
                                </div>
                                <div className="owner-dashboard-invitation-content">
                                    <h3>{t('owner.invitationTitle')}</h3>
                                    <p>
                                        <strong>{invitation.trainerFirstName} {invitation.trainerLastName}</strong> {t('owner.invitesYouToSchool')} <strong>{invitation.schoolName}</strong>
                                        {invitation.dogName && (
                                            <> {t('owner.forDog')} <strong>{invitation.dogName}</strong></>
                                        )}
                                    </p>
                                </div>
                                <div className="owner-dashboard-invitation-actions">
                                    <button
                                        className="owner-dashboard-invitation-btn owner-dashboard-invitation-btn-accept"
                                        onClick={() => handleAcceptInvitation(invitation.id)}
                                        disabled={invitationLoading}
                                    >
                                        <FiCheck size={16} /> {t('owner.accept')}
                                    </button>
                                    <button
                                        className="owner-dashboard-invitation-btn owner-dashboard-invitation-btn-reject"
                                        onClick={() => handleRejectInvitation(invitation.id)}
                                        disabled={invitationLoading}
                                    >
                                        <FiX size={16} /> {t('owner.reject')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {dashboardError && (
                    <Widget type="info" title={t('owner.error')} error={dashboardError} onRetry={refresh} />
                )}

                {dogs.length === 0 ? (
                    <div className="owner-dashboard-empty">
                        <Widget type="info" title={t('owner.noDogs')}>
                            <Widget.EmptyState
                                message={t('owner.noDogsMessage')}
                                icon={<FaDog size={48} />}
                                action={
                                    <Widget.Button onClick={() => navigate('/owner/my-dog')}>
                                        {t('owner.addDog')}
                                    </Widget.Button>
                                }
                            />
                        </Widget>
                    </div>
                ) : (
                    <div className="owner-dashboard-dogs">
                        {dogs.map((dog) => (
                            <div key={dog.dogId} className="owner-dashboard-dog-section">
                                <div className="owner-dashboard-dog-header">
                                    <h2>
                                        <FaDog className="owner-dashboard-dog-icon" />
                                        {dog.dogName}
                                    </h2>
                                    {dog.breed && <span className="owner-dashboard-breed">{dog.breed}</span>}
                                </div>

                                <WidgetGrid columns="2" gap="medium">
                                    {/* Soonest Training Widget */}
                                    <WidgetGrid.Item>
                                        <Widget
                                            type="schedule"
                                            title={t('owner.nextTraining')}
                                            headerAction={
                                                <Widget.Badge variant={dog.soonestTraining ? 'primary' : 'default'}>
                                                    {dog.soonestTraining?.type === 'group' ? t('owner.group') : t('owner.individual')}
                                                </Widget.Badge>
                                            }
                                        >
                                            {dog.soonestTraining ? (
                                                <div className="owner-dashboard-training-info">
                                                    <Widget.ListItem
                                                        primary={formatDate(dog.soonestTraining.startTime)}
                                                        secondary={`${formatTime(dog.soonestTraining.startTime)} - ${formatTime(dog.soonestTraining.endTime)}`}
                                                        icon={<FaCalendarAlt color="#7269FF" />}
                                                        status="info"
                                                    />
                                                    {dog.soonestTraining.serviceName && (
                                                        <Widget.InfoRow
                                                            label={t('owner.service')}
                                                            value={dog.soonestTraining.serviceName}
                                                        />
                                                    )}
                                                    {dog.soonestTraining.trainerName && (
                                                        <Widget.InfoRow
                                                            label={t('owner.trainer')}
                                                            value={dog.soonestTraining.trainerName}
                                                        />
                                                    )}
                                                    {dog.soonestTraining.hallName && (
                                                        <Widget.InfoRow
                                                            label={t('owner.location')}
                                                            value={dog.soonestTraining.hallName}
                                                        />
                                                    )}
                                                    <Widget.Button
                                                        variant="outline"
                                                        fullWidth
                                                        onClick={() => navigate('/owner/trainings')}
                                                    >
                                                        {t('owner.viewAllTrainings')}
                                                    </Widget.Button>
                                                </div>
                                            ) : (
                                                <Widget.EmptyState
                                                    message={t('owner.noTrainingsScheduled')}
                                                    icon={<FaCalendarAlt />}
                                                    action={
                                                        <Widget.Button
                                                            variant="primary"
                                                            onClick={() => navigate('/owner/calendar')}
                                                        >
                                                            {t('owner.bookTraining')}
                                                        </Widget.Button>
                                                    }
                                                />
                                            )}
                                        </Widget>
                                    </WidgetGrid.Item>

                                    {/* Exercise Stats Widget */}
                                    <WidgetGrid.Item>
                                        <Widget type="stat" title={t('owner.exerciseProgress')}>
                                            {dog.exerciseStats && dog.exerciseStats.totalExercises > 0 ? (
                                                <div className="owner-dashboard-exercise-stats">
                                                    <Widget.Progress
                                                        value={dog.exerciseStats.completionPercentage}
                                                        max={100}
                                                        label={t('owner.completedExercises')}
                                                        variant={getProgressVariant(dog.exerciseStats.completionPercentage)}
                                                        size="large"
                                                    />
                                                    <div className="owner-dashboard-stats-row">
                                                        <Widget.Stat
                                                            value={dog.exerciseStats.completedExercises}
                                                            label={t('owner.completed')}
                                                            size="small"
                                                        />
                                                        <Widget.Stat
                                                            value={dog.exerciseStats.pendingExercises}
                                                            label={t('owner.inProgress')}
                                                            size="small"
                                                        />
                                                        <Widget.Stat
                                                            value={dog.exerciseStats.totalExercises}
                                                            label={t('owner.total')}
                                                            size="small"
                                                        />
                                                    </div>
                                                    <Widget.Divider />
                                                    <Widget.Button
                                                        variant="outline"
                                                        fullWidth
                                                        icon={<FaDumbbell />}
                                                        onClick={() => navigate('/owner/my-dog')}
                                                    >
                                                        {t('owner.viewExercises')}
                                                    </Widget.Button>
                                                </div>
                                            ) : (
                                                <Widget.EmptyState
                                                    message={t('owner.noExercises')}
                                                    icon={<FaDumbbell />}
                                                />
                                            )}
                                        </Widget>
                                    </WidgetGrid.Item>

                                    {/* Pending Reports Widget */}
                                    <WidgetGrid.Item>
                                        <Widget
                                            type="info"
                                            title={t('owner.pendingReports')}
                                            headerAction={
                                                dog.pendingReports?.length > 0 && (
                                                    <Widget.Badge variant="warning">
                                                        {dog.pendingReports.length}
                                                    </Widget.Badge>
                                                )
                                            }
                                        >
                                            {dog.pendingReports && dog.pendingReports.length > 0 ? (
                                                <div className="owner-dashboard-pending-list">
                                                    {dog.pendingReports.slice(0, 3).map((report, index) => (
                                                        <Widget.ListItem
                                                            key={report.id || index}
                                                            primary={report.name}
                                                            secondary={report.recurrence ? `${t('owner.recurrence')}: ${report.recurrence.toLowerCase()}` : t('owner.oneTime')}
                                                            icon={<FaClipboardList color="#F59E0B" />}
                                                            status="warning"
                                                            onClick={() => navigate('/owner/my-dog')}
                                                        />
                                                    ))}
                                                    {dog.pendingReports.length > 3 && (
                                                        <p className="owner-dashboard-more-items">
                                                            + {dog.pendingReports.length - 3} {t('owner.more')}
                                                        </p>
                                                    )}
                                                    <Widget.Button
                                                        variant="success"
                                                        fullWidth
                                                        onClick={() => navigate('/owner/my-dog')}
                                                    >
                                                        {t('owner.fillReports')}
                                                    </Widget.Button>
                                                </div>
                                            ) : (
                                                <Widget.EmptyState
                                                    message={t('owner.noReports')}
                                                    icon={<FaClipboardList />}
                                                />
                                            )}
                                        </Widget>
                                    </WidgetGrid.Item>

                                    {/* Pending Surveys Widget */}
                                    <WidgetGrid.Item>
                                        <Widget
                                            type="info"
                                            title={t('owner.pendingSurveys')}
                                            headerAction={
                                                dog.pendingSurveys?.length > 0 && (
                                                    <Widget.Badge variant="error">
                                                        {dog.pendingSurveys.length}
                                                    </Widget.Badge>
                                                )
                                            }
                                        >
                                            {dog.pendingSurveys && dog.pendingSurveys.length > 0 ? (
                                                <div className="owner-dashboard-pending-list">
                                                    {dog.pendingSurveys.slice(0, 3).map((survey, index) => (
                                                        <Widget.ListItem
                                                            key={survey.id || index}
                                                            primary={survey.name}
                                                            secondary={`${survey.questionCount || 0} ${t('owner.questions')}`}
                                                            icon={<FaFileAlt color="#EF4444" />}
                                                            status="error"
                                                            onClick={() => navigate('/owner/my-dog')}
                                                        />
                                                    ))}
                                                    {dog.pendingSurveys.length > 3 && (
                                                        <p className="owner-dashboard-more-items">
                                                            + {dog.pendingSurveys.length - 3} {t('owner.more')}
                                                        </p>
                                                    )}
                                                    <Widget.Button
                                                        variant="primary"
                                                        fullWidth
                                                        onClick={() => navigate('/owner/my-dog')}
                                                    >
                                                        {t('owner.fillSurveys')}
                                                    </Widget.Button>
                                                </div>
                                            ) : (
                                                <Widget.EmptyState
                                                    message={t('owner.noSurveys')}
                                                    icon={<FaFileAlt />}
                                                />
                                            )}
                                        </Widget>
                                    </WidgetGrid.Item>
                                </WidgetGrid>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </OwnerLayout>
    );
};

export default OwnerDashboard;